import { useState } from 'react';
import { ChartTooltip } from '../bookings/chartParts';
import { axisTicks, fmtEur, fmtEurShort, niceScale } from './financesData';

export interface YoYMonth { mi: number; label: string; full: string; prev: number; cur: number }

// Year-on-year grouped vertical bars, by calendar month, with a euro y-axis and
// gridlines. Prior year muted, focus year in brand. Hovering a month lifts a
// tooltip with both years and the delta. Scale logic (niceScale/axisTicks) is
// shared with the trend chart.
export function FinYoY({ months, prevYear, curYear }: { months: YoYMonth[]; prevYear: number; curYear: number }) {
  const [hi, setHi] = useState<number | null>(null);
  // The axis floors at ZERO. Revenue earned cannot be negative — a night served
  // is worth what it is worth — and the only thing that could ever produce a
  // negative month was the cash basis, which was removed before shipping.
  //
  // A "bars anchored at the zero line" version of this lived here and was cut
  // (review, PR #151): `.fin-yoy-bar` is a flex child with no `position`, so its
  // inline `bottom` was silently dropped and only `height` was ever applied.
  // Anchoring that nothing can reach is worse than no anchoring, because it
  // reads as handling that is present. If a signed series ever returns here,
  // give the bar a positioning context FIRST — the trend chart's SVG transform
  // (FinancesTrendChart) handles sign correctly and is the model to copy.
  const { yMin, yMax, step } = niceScale(Math.max(1, ...months.flatMap((m) => [m.prev, m.cur])));
  const ticks = axisTicks(yMax, step, yMin);
  const hp = hi != null ? months[hi]! : null;
  const delta = hp && hp.prev ? (hp.cur - hp.prev) / hp.prev : null;
  const span = yMax - yMin;
  // Where a value sits as a % of the area height, measured from the bottom.
  const pct = (v: number) => `${((v - yMin) / span) * 100}%`;
  // A bar grows upward from the floor, which IS the zero line here.
  const bar = (v: number) => ({ height: pct(v) });
  // Anchor the shared chart tooltip to the hovered month's tallest bar — same
  // left%/top% + above/below flip the trend chart uses.
  const leftPct = hi != null ? ((hi + 0.5) / months.length) * 100 : 0;
  const anchorPct = hp ? Math.max(0, Math.min(100, 100 - ((Math.max(hp.prev, hp.cur) - yMin) / span) * 100)) : 0;
  return (
    <div className="fin-yoy">
      <div className="fin-yoy-area" onMouseLeave={() => setHi(null)}>
        {ticks.map((t, i) => (
          <div key={'g' + i} className="fin-yoy-grid" style={{ bottom: pct(t) }} />
        ))}
        {ticks.map((t, i) => (
          <span key={'y' + i} className="fin-yoy-yval mono" style={{ bottom: pct(t) }}>{fmtEurShort(t)}</span>
        ))}
        {months.map((m, i) => (
          <div className={'fin-yoy-col' + (hi === i ? ' on' : '')} key={m.mi} onMouseEnter={() => setHi(i)}>
            <div className="fin-yoy-bar prev" style={bar(m.prev)} />
            <div className="fin-yoy-bar cur" style={bar(m.cur)} />
          </div>
        ))}
        {hp && (
          <ChartTooltip
            head={hp.full}
            rows={[
              { key: 'prev', sw: 'var(--border-strong)', label: prevYear, value: fmtEur(hp.prev) },
              { key: 'cur', sw: 'var(--brand)', label: curYear, value: hp.cur ? fmtEur(hp.cur) : '—' },
            ]}
            total={
              delta != null && hp.cur
                ? {
                    label: `vs ${prevYear}`,
                    value: `${delta >= 0 ? '▲' : '▼'} ${Math.abs(delta * 100).toFixed(1)}%`,
                    valueStyle: { color: delta >= 0 ? 'var(--primary-deep)' : 'var(--alert-deep)' },
                  }
                : undefined
            }
            leftPct={leftPct}
            anchorPct={anchorPct}
          />
        )}
      </div>
      <div className="fin-yoy-labels">
        {months.map((m, i) => (
          <div className={'fin-yoy-label' + (hi === i ? ' on' : '')} key={m.mi}>{m.label}</div>
        ))}
      </div>
    </div>
  );
}
